import { useEffect, useState } from "react";
import { CirclePlus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { apiClient } from "@/lib/api";

const blankQuestion = () => ({
  id: `question_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
  label: "",
  placeholder: "",
  type: "textarea",
  required: true,
});

export default function QuestionsPage() {
  const [questions, setQuestions] = useState([]);
  const [version, setVersion] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadQuestions = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get("/questions/active");
      setQuestions(response.data.question_set?.questions || []);
      setVersion(response.data.question_set?.version || 1);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Nie udało się pobrać pytań.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadQuestions();
  }, []);

  const updateQuestion = (index, field, value) => {
    setQuestions((current) => current.map((question, currentIndex) => (currentIndex === index ? { ...question, [field]: value } : question)));
  };

  const addQuestion = () => setQuestions((current) => [...current, blankQuestion()]);
  const removeQuestion = (index) => setQuestions((current) => current.filter((_, currentIndex) => currentIndex !== index));

  const saveQuestions = async () => {
    const invalidQuestion = questions.find((question) => !question.id?.trim() || !question.label?.trim());
    if (invalidQuestion) {
      toast.error("Każde pytanie musi mieć ID i treść.");
      return;
    }

    setSaving(true);
    try {
      const response = await apiClient.put("/questions/active", { questions });
      setQuestions(response.data.question_set.questions || []);
      setVersion(response.data.question_set.version || version + 1);
      toast.success("Nowa wersja pytań została opublikowana.");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Nie udało się zapisać pytań.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="questions-management-page">
      <Card className="surface-noise border-border bg-card shadow-[0_10px_30px_rgba(0,0,0,0.35)]">
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-[0.24em] text-primary/80">Panel pytań</p>
              <CardTitle className="mt-2 text-3xl font-semibold tracking-tight">Edytuj formularz rekrutacyjny</CardTitle>
            </div>
            <div className="rounded-2xl border border-primary/20 bg-primary/10 px-4 py-3 text-right">
              <p className="text-xs uppercase tracking-[0.24em] text-primary/80">Aktywna wersja</p>
              <p className="mt-1 text-lg font-semibold">v{version}</p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          Tylko użytkownicy z wyższą rangą mogą aktualizować pytania. Po zapisaniu kandydaci natychmiast zobaczą nową wersję formularza.
        </CardContent>
      </Card>

      {loading ? (
        <div className="space-y-4">
          <div className="h-40 rounded-3xl bg-muted" />
          <div className="h-40 rounded-3xl bg-muted" />
        </div>
      ) : (
        <div className="space-y-4">
          {questions.map((question, index) => (
            <Card key={question.id} className="border-border bg-card">
              <CardHeader>
                <div className="flex items-center justify-between gap-3">
                  <CardTitle className="text-lg">Pytanie #{index + 1}</CardTitle>
                  <Button
                    variant="ghost"
                    onClick={() => removeQuestion(index)}
                    data-testid={`question-remove-button-${index}`}
                  >
                    <Trash2 className="mr-2 h-4 w-4" /> Usuń
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2 md:col-span-2">
                  <label className="text-sm font-medium text-foreground">ID pytania</label>
                  <Input
                    value={question.id}
                    onChange={(event) => updateQuestion(index, "id", event.target.value)}
                    placeholder="np. motivation"
                    data-testid={`question-id-input-${index}`}
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <label className="text-sm font-medium text-foreground">Treść pytania</label>
                  <Textarea
                    value={question.label}
                    onChange={(event) => updateQuestion(index, "label", event.target.value)}
                    className="min-h-[110px]"
                    data-testid={`question-label-input-${index}`}
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <label className="text-sm font-medium text-foreground">Placeholder</label>
                  <Textarea
                    value={question.placeholder}
                    onChange={(event) => updateQuestion(index, "placeholder", event.target.value)}
                    className="min-h-[90px]"
                    data-testid={`question-placeholder-input-${index}`}
                  />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        <Button variant="secondary" onClick={addQuestion} data-testid="add-question-button">
          <CirclePlus className="mr-2 h-4 w-4" /> Dodaj pytanie
        </Button>
        <Button onClick={saveQuestions} disabled={saving || loading} data-testid="questions-editor-save-button">
          <Save className="mr-2 h-4 w-4" /> {saving ? "Zapisywanie..." : "Opublikuj pytania"}
        </Button>
      </div>
    </div>
  );
}
